// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Chart.js
// Interactive candlestick charts. Renders without nav when embedded in Workspace.
// "Company ↗" in the controls bar opens /company for the current ticker in a new tab.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ChartPatternSettings from './ChartPatternSettings';
import { readPatternSettings, normaliseSettings, detectPatterns, drawPatternOverlays } from './chartPatterns';

// Highcharts loaded via CDN in public/index.html
const HC = window.Highcharts;
// Highcharts needs real colour strings, so the theme variables are read each time a chart is built.
const css = n => getComputedStyle(document.documentElement).getPropertyValue(n).trim();
const hcTheme = () => {
  const [bg, text, muted, border, up, down] = ['--surface', '--text', '--text-muted', '--border', '--up', '--down'].map(css);
  const axis = { lineColor: border, tickColor: border, gridLineColor: border, labels: { style: { color: muted } } };
  return {
    chart: { backgroundColor: bg }, xAxis: axis, yAxis: axis,
    legend: { itemStyle: { color: text }, itemHoverStyle: { color: text }, itemHiddenStyle: { color: muted } },
    tooltip: { backgroundColor: bg, borderColor: border, style: { color: text } },
    navigator: { outlineColor: border, xAxis: { gridLineColor: border, labels: { style: { color: muted } } } },
    scrollbar: { barBackgroundColor: border, barBorderColor: border, buttonBackgroundColor: bg, buttonBorderColor: border,
                 buttonArrowColor: muted, rifleColor: muted, trackBackgroundColor: bg, trackBorderColor: border },
    plotOptions: { candlestick: { color: down, upColor: up, lineColor: down, upLineColor: up } },
  };
};

const SEARCH_DEBOUNCE = 300;
const AUTO_SAVE_DELAY = 3000;
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// ── Touch-tap guard ──────────────────────────────────────────────────────────
// Stock-tools drawing bindings are driven entirely by `click` events on the
// chart container (Highcharts NavigationBindings listens for container clicks,
// never touchstart). Mobile browsers only synthesize that click when the DOM
// stays quiet during the tap's hover phase — if a point mouseOver handler
// re-renders HTML (our OHLCV readout), the tap is treated as a hover and the
// click is swallowed, silently breaking every drawing tool. Track touches so
// hover handlers can skip touch-originated events; taps still update the
// readout via the point `click` event, which fires after the gesture ends.
let lastTouchAt = 0;
window.addEventListener('touchstart', () => { lastTouchAt = Date.now(); }, { passive: true, capture: true });
const touchish = (e) =>
  (Date.now() - lastTouchAt) < 800 ||
  !!(e && (e.pointerType === 'touch' || (e.touches && e.touches.length) || (e.type || '').indexOf('touch') === 0));

// Popup touch guard: the stock-tools popup (indicator dialog, annotation edit
// box) lives inside the chart container, so touches in it reach the chart's
// pointer handlers, which preventDefault once a finger moves a few px — the
// synthesized click is cancelled and mobile inputs can never be focused.
// Bypass the chart for any touch that begins inside a popup.
if (HC.Pointer && !HC.Pointer.prototype._popupTouchPatched) {
  HC.Pointer.prototype._popupTouchPatched = true;
  ['onContainerTouchStart', 'onContainerTouchMove'].forEach((fn) => {
    const orig = HC.Pointer.prototype[fn];
    HC.Pointer.prototype[fn] = function (e) {
      if (e.target.closest?.('.highcharts-popup')) return;
      return orig.call(this, e);
    };
  });
}

const S = {
  page:       { height: '100vh', overflow: 'hidden', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  nav:        { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 20px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  logo:       { fontFamily: "'JetBrains Mono',monospace", fontSize: '12px', fontWeight: 500, letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase' },
  navRight:   { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: 'var(--text-muted)' },
  logoutBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '13px', fontFamily: "'Space Grotesk',sans-serif", padding: '4px 10px', borderRadius: 'var(--radius)' },
  controls:   { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, flexWrap: 'wrap', minHeight: '52px' },
  searchWrap: { position: 'relative', flex: '0 0 300px' },
  searchInput:{ width: '100%', padding: '7px 12px', fontSize: '13px', fontFamily: "'JetBrains Mono',monospace", border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none', letterSpacing: '0.05em' },
  dropdown:   { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', zIndex: 100, maxHeight: '280px', overflowY: 'auto' },
  dropItem:   { padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '13px', borderBottom: '1px solid var(--border)' },
  dropSymbol: { fontFamily: "'JetBrains Mono',monospace", fontWeight: 500, fontSize: '13px', color: 'var(--text)', flexShrink: 0 },
  dropName:   { fontSize: '12px', color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge:      { fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: '3px', fontWeight: 600, flexShrink: 0 },
  searchDot:  { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1s infinite' },
  infoBar:    { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '6px 16px', display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0, minHeight: '34px' },
  infoIdent:  { fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  infoNums:   { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: '14px', rowGap: '3px' },
  infoItem:   { display: 'inline-flex', gap: '5px', alignItems: 'baseline', whiteSpace: 'nowrap' },
  infoDivider:{ width: '1px', height: '12px', background: 'var(--border)', alignSelf: 'center' },
  infoMore:   { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'nowrap', marginLeft: 'auto' },
  infoLabel:  { fontSize: '10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.04em' },
  infoValue:  { fontSize: '12px', fontWeight: 600, color: 'var(--text)' },
  infoLink:   { fontSize: '12px', fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' },
  chartWrap:  { flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden' },
  chartDiv:   { width: '100%', height: '100%', position: 'absolute', inset: 0 },
  overlay:    { position: 'absolute', inset: 0, background: 'var(--veil)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, backdropFilter: 'blur(2px)' },
  spinner:    { width: '32px', height: '32px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },
  saveStatus: { position: 'absolute', top: '8px', left: '8px', zIndex: 30, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, pointerEvents: 'none' },
  resetBtn:   { position: 'absolute', zIndex: 9999, width: '32px', height: '32px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s' },
  // Analyst bar
  analystWrap:    { display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 },
  analystTrack:   { position: 'relative', width: '180px', height: '6px', borderRadius: '3px', background: 'var(--border)', flexShrink: 0 },
  analystPrice:   { fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 },
  analystCounts:  { fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 },
  analystTooltip: { position: 'fixed', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 10px', fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", color: 'var(--text)', boxShadow: 'var(--shadow)', zIndex: 9999, pointerEvents: 'none', minWidth: '160px', lineHeight: '1.6' },
  // Add to list
  addListBtn:   { background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '5px 12px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:        { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius)*2)', padding: '28px', width: '420px', maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle:   { fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: 'var(--text)' },
  modalInput:   { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Space Grotesk',sans-serif", boxSizing: 'border-box', marginTop: '4px' },
  modalLabel:   { fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'JetBrains Mono',monospace", display: 'block', marginBottom: '4px' },
  modalSelect:  { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace", boxSizing: 'border-box' },
  modalFooter:  { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' },
  modalError:   { color: 'var(--error)', fontSize: '12px', marginTop: '6px' },
  modalSuccess: { color: 'var(--success)', fontSize: '12px', marginTop: '6px' },
  btnSm:        { background: 'var(--accent)', color: 'var(--on-accent)', border: 'none', borderRadius: 'var(--radius)', padding: '4px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  btnGhost:     { background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
  // OHLCV hover display
  // Overlaid top-left of the chart, clear of the stock-tools toolbar; only while a candle is hovered/tapped.
  ohlcvWrap:  { position: 'absolute', top: '6px', left: '64px', right: '70px', zIndex: 20, pointerEvents: 'none', display: 'flex', flexWrap: 'wrap', gap: '2px 10px', alignItems: 'baseline' },
  ohlcvPill:  { display: 'inline-flex', gap: '3px', alignItems: 'baseline', background: 'var(--surface)', opacity: 0.92, borderRadius: '3px', padding: '0 3px' },
  ohlcvLabel: { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' },
  ohlcvValue: { fontSize: '11px', fontWeight: 400, color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace" },
};

const KEYFRAMES = `
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
/* dvh tracks the visible viewport on mobile (100vh includes browser chrome).
   Browsers without dvh support drop this and keep the inline 100vh. */
.chart-page { height: 100dvh !important; }
/* Mobile: search, + List and Company on one row (ticker label hidden - it's in the box).
   Info bar: desktop shows every number (.cp-desk); phones a scrolling essentials row
   with More (.cp-mob), the rest in a block below (.cp-mob-rest). */
@media (max-width: 768px) {
  .cp-controls { gap: 8px !important; padding: 6px 10px !important; }
  .cp-search { flex: 1 1 140px !important; min-width: 0; }
  .cp-ticker { display: none; }
  .cp-infobar { padding: 5px 10px !important; }
  .cp-desk { display: none !important; }
  .cp-mob > * { flex-shrink: 0; }
  .cp-mob:not(.cp-mob-rest) { flex-wrap: nowrap !important; overflow-x: auto; scrollbar-width: none; }
  .cp-mob::-webkit-scrollbar { display: none; }
}
@media (min-width: 769px) { .cp-mob { display: none !important; } }
`;

const HC_CSS = `
.highcharts-save-annotations .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="18" text-anchor="middle" font-size="16" fill="%23666">💾</text></svg>') !important; }
.highcharts-toggle-autosave .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="%23dc3545" stroke-width="2"/><text x="12" y="18" text-anchor="middle" font-size="14" font-weight="bold" fill="%23dc3545">A</text></svg>') !important; }
.highcharts-toggle-autosave.is-active .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="18" text-anchor="middle" font-size="14" font-weight="bold" fill="%238A2BE2">A</text></svg>') !important; }
.highcharts-grouping-1d .highcharts-menu-item-btn  { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%23666">1D</text></svg>') !important; }
.highcharts-grouping-1w .highcharts-menu-item-btn  { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%23666">1W</text></svg>') !important; }
.highcharts-grouping-1m .highcharts-menu-item-btn  { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%23666">1M</text></svg>') !important; }
.highcharts-grouping-6m .highcharts-menu-item-btn  { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%23666">6M</text></svg>') !important; }
.highcharts-grouping-12m .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="9" font-weight="bold" fill="%23666">12M</text></svg>') !important; }
.highcharts-grouping-1d.is-active  .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%238A2BE2">1D</text></svg>') !important; }
.highcharts-grouping-1w.is-active  .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%238A2BE2">1W</text></svg>') !important; }
.highcharts-grouping-1m.is-active  .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%238A2BE2">1M</text></svg>') !important; }
.highcharts-grouping-6m.is-active  .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="%238A2BE2">6M</text></svg>') !important; }
.highcharts-grouping-12m.is-active .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text x="12" y="16" text-anchor="middle" font-size="9" font-weight="bold" fill="%238A2BE2">12M</text></svg>') !important; }
.highcharts-i-range .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><line x1="6" y1="6" x2="18" y2="6" stroke="%23666" stroke-width="2"/><line x1="12" y1="6" x2="12" y2="18" stroke="%23666" stroke-width="2"/><line x1="6" y1="18" x2="18" y2="18" stroke="%23666" stroke-width="2"/></svg>') !important; }
.highcharts-ell-arc .highcharts-menu-item-btn { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M4 18 Q 12 2 20 18" fill="none" stroke="%23666" stroke-width="2"/></svg>') !important; }
.highcharts-active .highcharts-menu-item-btn { background-color: var(--accent-dim); border-radius: 4px; }
/* Let touches on control points become drags instead of scrolling/panning. */
.highcharts-control-point, .highcharts-control-point * { touch-action: none !important; }
.highcharts-popup, .highcharts-popup * { touch-action: auto !important; }
`;

// ── Formatting ───────────────────────────────────────────────────────────────
function fmt(v, dp = 2) {
  if (v == null || v === '') return null;
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function fmtLarge(v) {
  if (!v) return null;
  if (v >= 1e12) return '$' + fmt(v / 1e12, 2) + 'T';
  if (v >= 1e9)  return '$' + fmt(v / 1e9,  2) + 'B';
  if (v >= 1e6)  return '$' + fmt(v / 1e6,  2) + 'M';
  return '$' + fmt(v, 0);
}
function fmtPct(v) { return v != null ? fmt(v * 100, 2) + '%' : null; }

// Full precision price — no rounding, minimum 2dp, $ prefix
function fmtPrice(v) {
  if (v == null) return null;
  const n = Number(v);
  const str = n.toString();
  const dp = str.includes('.') ? str.split('.')[1].length : 0;
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: Math.max(2, dp), maximumFractionDigits: Math.max(2, dp) });
}

// ── Info bar ─────────────────────────────────────────────────────────────────
// ── Info bar ─────────────────────────────────────────────────────────────────
// Two plain text lines, no tiles: identity (name | sector | industry), then the
// numbers in fixed groups: valuation │ profitability │ risk │ events │ link.
// Missing / 'Unknown' values are not shown. Desktop shows everything; phones get
// one scrolling row of essentials + More at its end, the rest in a block below
// (CSS .cp-desk / .cp-mob in KEYFRAMES). Full set also on the company page.
const known = v => v != null && v !== '' && v !== 'Unknown';

function InfoItem({ label, value }) {
  if (!known(value)) return null;
  return (
    <span style={S.infoItem}>
      <span style={S.infoLabel}>{label}</span>
      <span style={S.infoValue}>{value}</span>
    </span>
  );
}

// groups: [[{label, value, href?}]] - empty items and empty groups dropped, divider between groups.
function InfoGroups({ groups }) {
  const live = groups.map(g => g.filter(it => known(it.value))).filter(g => g.length);
  return live.map((g, gi) => (
    <React.Fragment key={gi}>
      {gi > 0 && <span style={S.infoDivider} />}
      {g.map(it => it.href
        ? <a key={it.label} href={it.href} target="_blank" rel="noopener noreferrer" style={S.infoLink}>{it.value}</a>
        : <InfoItem key={it.label} label={it.label} value={it.value} />)}
    </React.Fragment>
  ));
}

const fmtDay = d => {
  if (!d) return null;
  const t = new Date(d + 'T00:00:00');
  if (isNaN(t)) return d;
  const opts = { day: 'numeric', month: 'short' };
  if (t.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return t.toLocaleDateString('en-AU', opts);
};

function InfoBar({ info, isRatio, pair }) {
  const [more, setMore] = useState(false);
  if (isRatio) return (
    <div className="cp-infobar" style={S.infoBar}><div style={S.infoNums}><InfoItem label="RATIO" value={pair} /></div></div>
  );
  if (!info) return <div className="cp-infobar" style={S.infoBar} />;

  const { name, asset_type, fundamentals: f = {}, snapshot: snap = {}, profile: p = {} } = info;
  const isETF = ['ETF', 'etf'].includes(asset_type);
  const web = p.website_url && { label: 'WEB', href: p.website_url,
                                 value: p.website_url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') };

  // Identity: GICS sector | group, plus the exchange's own industry when it adds something (US).
  let idParts;
  if (isETF) idParts = [p.sector, f.provider, f.index_tracked];
  else {
    const gics = [...new Set([p.gics_sector, p.gics_group].filter(known))];
    const own = known(p.industry) && !gics.includes(p.industry) ? ` (${p.industry})` : '';
    idParts = [gics.length ? gics.join(' | ') + own : [p.sector, p.industry].filter(known).join(' | ')];
  }
  const ident = [name, ...idParts].filter(known).join(' | ');

  const groups = isETF ? [
    [{ label: 'MER', value: f.expense_ratio != null ? fmt(f.expense_ratio * 100, 2) + '%' : null },
     { label: 'AUM', value: fmtLarge(f.aum_usd) }, { label: 'YIELD', value: fmtPct(f.ttm_yield) }],
    [{ label: 'RSI', value: snap.rsi14 ? fmt(snap.rsi14, 1) : null }],
    [web || {}],
  ] : [
    [{ label: 'MKT CAP', value: fmtLarge(f.market_cap) }, { label: 'P/E', value: fmt(f.pe_ratio, 1) },
     { label: 'P/B', value: fmt(f.price_to_book, 1) }, { label: 'EPS', value: fmt(f.eps_ttm) },
     { label: 'YIELD', value: fmtPct(f.dividend_yield) }],
    [{ label: 'MARGIN', value: fmtPct(f.profit_margin) }, { label: 'ROE', value: fmtPct(f.return_on_equity_ttm) }],
    [{ label: 'BETA', value: fmt(f.beta) }, { label: 'RSI', value: snap.rsi14 ? fmt(snap.rsi14, 1) : null }],
    [{ label: 'NEXT EARN', value: fmtDay(f.next_earnings_date) },
     { label: 'TARGET', value: f.wall_street_target_price != null ? '$' + fmt(f.wall_street_target_price) : null }],
    [web || {}],
  ];
  const ESSENTIAL = isETF ? ['MER', 'AUM', 'YIELD'] : ['MKT CAP', 'P/E', 'YIELD', 'NEXT EARN'];
  const ess = groups.flat().filter(it => ESSENTIAL.includes(it.label));
  const rest = groups.map(g => g.filter(it => !ESSENTIAL.includes(it.label)));
  const hasRest = rest.some(g => g.some(it => known(it.value)));

  return (
    <div className="cp-infobar" style={S.infoBar}>
      <div style={S.infoIdent} title={ident}>{ident}</div>
      <div className="cp-desk" style={S.infoNums}><InfoGroups groups={groups} /></div>
      <div className="cp-mob" style={S.infoNums}>
        <InfoGroups groups={[ess]} />
        {hasRest && <button onClick={() => setMore(m => !m)} style={S.infoMore}>{more ? 'Less ▴' : 'More ▾'}</button>}
      </div>
      {more && <div className="cp-mob cp-mob-rest" style={S.infoNums}><InfoGroups groups={rest} /></div>}
    </div>
  );
}

// ── OHLCV Hover Display ───────────────────────────────────────────────────────
function OhlcvDisplay({ candle }) {
  if (!candle) return null;
  const { date, open, high, low, close, volume } = candle;
  const item = (label, value) => (
    <span key={label} style={S.ohlcvPill}>
      <span style={S.ohlcvLabel}>{label}</span>
      <span style={S.ohlcvValue}>{value ?? '—'}</span>
    </span>
  );
  return (
    <div style={S.ohlcvWrap}>
      {item('O', fmtPrice(open))}
      {item('H', fmtPrice(high))}
      {item('L', fmtPrice(low))}
      {item('C', fmtPrice(close))}
      {item('VOL', volume != null ? Number(volume).toLocaleString(undefined, { maximumFractionDigits: 0 }) : null)}
      {item('', date)}
    </div>
  );
}

// ── Add To List Modal ─────────────────────────────────────────────────────────
function AddToListModal({ ticker, onClose }) {
  const [lists,     setLists]     = useState([]);
  const [mode,      setMode]      = useState('existing');
  const [selListId, setSelListId] = useState('');
  const [newName,   setNewName]   = useState('');
  const [busy,      setBusy]      = useState(false);
  const [msg,       setMsg]       = useState('');
  const [err,       setErr]       = useState('');

  useEffect(() => {
    api.get('/lists').then(r => {
      const l = r.data.lists || [];
      setLists(l);
      if (l.length > 0) setSelListId(String(l[0].id));
      else setMode('new');
    }).catch(() => setMode('new'));
  }, []);

  const submit = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      let listId;
      if (mode === 'new') {
        if (!newName.trim()) { setErr('List name required'); setBusy(false); return; }
        const r = await api.post('/lists', { name: newName.trim() });
        listId = r.data.list.id;
      } else {
        listId = parseInt(selListId);
        if (!listId) { setErr('Select a list'); setBusy(false); return; }
      }
      await api.post(`/lists/${listId}/items`, { ticker }).catch(e => {
        if (e.response?.status !== 409) throw e;
      });
      setMsg('Added to list');
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add ticker');
    } finally { setBusy(false); }
  };

  return (
    <div style={S.modalOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal}>
        <div style={S.modalTitle}>Add {ticker} to list</div>

        {lists.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
            <button
              style={{ ...S.btnSm, background: mode === 'existing' ? 'var(--accent)' : 'var(--bg)', color: mode === 'existing' ? 'var(--on-accent)' : 'var(--text-muted)', border: '1px solid var(--border)' }}
              onClick={() => setMode('existing')}
            >Existing List</button>
            <button
              style={{ ...S.btnSm, background: mode === 'new' ? 'var(--accent)' : 'var(--bg)', color: mode === 'new' ? 'var(--on-accent)' : 'var(--text-muted)', border: '1px solid var(--border)' }}
              onClick={() => setMode('new')}
            >New List</button>
          </div>
        )}

        {mode === 'existing' && lists.length > 0 ? (
          <div>
            <label style={S.modalLabel}>Select List</label>
            <select style={S.modalSelect} value={selListId} onChange={e => setSelListId(e.target.value)}>
              {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.item_count} tickers)</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label style={S.modalLabel}>New List Name</label>
            <input style={S.modalInput} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. ASX Watchlist" maxLength={100} />
          </div>
        )}

        {err && <div style={S.modalError}>{err}</div>}
        {msg && <div style={S.modalSuccess}>{msg}</div>}

        <div style={S.modalFooter}>
          <button style={S.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={S.btnSm} onClick={submit} disabled={busy}>{busy ? 'Adding…' : 'Add to List'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Analyst Bar ───────────────────────────────────────────────────────────────
function AnalystBar({ ticker, currentPrice }) {
  const [data,    setData]    = useState(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!ticker || ticker.includes('/')) { setData(null); return; }
    api.get(`/chart/analyst?ticker=${encodeURIComponent(ticker)}`)
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [ticker]);

  if (!data || data.ratings.length === 0) return null;

  const { ratings, counts } = data;
  const prices = ratings.map(r => r.price_target);
  const lo     = Math.min(...prices, ...(currentPrice ? [currentPrice] : []));
  const hi     = Math.max(...prices, ...(currentPrice ? [currentPrice] : []));
  const range  = hi - lo || 1;

  const pct = v => Math.max(0, Math.min(1, (v - lo) / range));

  const dotColor = (r) => {
    if (!currentPrice) return 'var(--accent)';
    return r.price_target > currentPrice ? 'var(--up)' : r.price_target < currentPrice ? 'var(--down)' : 'var(--accent)';
  };

  const handleMouseMove  = (e, r) => setTooltip({ x: e.clientX, y: e.clientY, r });
  const handleMouseLeave = () => setTooltip(null);

  const currency = ratings[0]?.currency || '';

  return (
    <div style={S.analystWrap}>
      <span style={S.analystPrice}>{currency}{fmt(lo)}</span>
      <div style={S.analystTrack}>
        {currentPrice && (
          <>
            <div style={{ position:'absolute', left:0, top:0, height:'100%', width: (pct(currentPrice)*100)+'%', background:'var(--down)', borderRadius:'3px 0 0 3px', opacity:0.35 }} />
            <div style={{ position:'absolute', left:(pct(currentPrice)*100)+'%', top:0, height:'100%', right:0, background:'var(--up)', borderRadius:'0 3px 3px 0', opacity:0.35 }} />
          </>
        )}

        {ratings.map((r, i) => (
          <div
            key={i}
            onMouseMove={e => handleMouseMove(e, r)}
            onMouseLeave={handleMouseLeave}
            style={{
              position: 'absolute',
              left: `calc(${pct(r.price_target)*100}% - 4px)`,
              top: '-3px',
              width: '8px', height: '8px',
              borderRadius: '50%',
              background: dotColor(r),
              border: '1.5px solid var(--bg)',
              cursor: 'default',
              zIndex: 2,
            }}
          />
        ))}

        {currentPrice && (
          <div
            onMouseMove={e => setTooltip({ x: e.clientX, y: e.clientY, currentPrice: true })}
            onMouseLeave={handleMouseLeave}
            style={{
              position: 'absolute',
              left: `calc(${pct(currentPrice)*100}% - 5px)`,
              top: '-4px',
              width: '10px', height: '10px',
              borderRadius: '50%',
              background: 'var(--accent)',
              border: '2px solid var(--bg)',
              zIndex: 3,
              cursor: 'default',
            }}
          />
        )}
      </div>
      <span style={S.analystPrice}>{currency}{fmt(hi)}</span>

      <span style={S.analystCounts}>
        <span style={{ color:'var(--up)' }}>B {counts.Buy}</span>
        {' · '}
        <span style={{ color:'var(--text-muted)' }}>H {counts.Hold}</span>
        {' · '}
        <span style={{ color:'var(--down)' }}>S {counts.Sell}</span>
      </span>

      {tooltip && !tooltip.currentPrice && (
        <div style={{ ...S.analystTooltip, left: tooltip.x + 12, top: tooltip.y - 8 }}>
          <div style={{ fontWeight:700, marginBottom:'2px' }}>{tooltip.r.firm}</div>
          {tooltip.r.analyst && <div style={{ color:'var(--text-muted)' }}>{tooltip.r.analyst}</div>}
          <div>{tooltip.r.rating}{tooltip.r.action ? ` · ${tooltip.r.action}` : ''}</div>
          <div style={{ color:'var(--accent)', fontWeight:700 }}>{currency}{fmt(tooltip.r.price_target)}</div>
          <div style={{ color:'var(--text-muted)' }}>{tooltip.r.date}</div>
          {tooltip.r.upside_downside != null && (
            <div style={{ color: tooltip.r.upside_downside >= 0 ? 'var(--up)' : 'var(--down)' }}>
              {tooltip.r.upside_downside >= 0 ? '+' : ''}{fmt(tooltip.r.upside_downside, 1)}%
            </div>
          )}
        </div>
      )}
      {tooltip && tooltip.currentPrice && currentPrice && (
        <div style={{ ...S.analystTooltip, left: tooltip.x + 12, top: tooltip.y - 8 }}>
          <div style={{ fontWeight:700, marginBottom:'2px', color:'var(--accent)' }}>Current Price</div>
          <div style={{ fontWeight:700 }}>{currency}{fmt(currentPrice)}</div>
        </div>
      )}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function ChartPage({
  embedded       = false,
  initialTicker  = null,
  refreshKey     = 0,
  onTickerChange = null,
}) {

  const { user } = useAuth();
  const initTicker = initialTicker || new URLSearchParams(window.location.search).get('t') || 'BHP:AU';

  const [ticker,        setTicker]        = useState(initTicker);
  const [inputVal,      setInputVal]      = useState(initTicker);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [searching,     setSearching]     = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [chartInfo,     setChartInfo]     = useState(null);
  const [saveStatus,    setSaveStatus]    = useState({ text: '', color: '' });
  const [autoSave,      setAutoSave]      = useState(() => localStorage.getItem('autoSave') === 'true');
  const [showListModal, setShowListModal] = useState(false);
  const [showPatternPanel, setShowPatternPanel] = useState(false);
  const [patternSettings, setPatternSettings] = useState(() => readPatternSettings(user?.id || 'guest'));
  const [patternsLoaded, setPatternsLoaded] = useState(false);
  const [hoveredCandle, setHoveredCandle] = useState(null);

  const patternBarsRef = useRef([]);
  const patternMatchesRef = useRef([]);
  const patternSettingsRef = useRef(patternSettings);
  const patternTouchedRef = useRef(false);
  const chartRef      = useRef(null);
  const chartDivRef   = useRef(null);
  const resetBtnRef   = useRef(null);
  const autoSaveTimer = useRef(null);
  const searchTimer   = useRef(null);
  const abortRef      = useRef(null);
  const autoSaveRef   = useRef(autoSave);
  // Stable ref so Highcharts closures always call the current setter
  const setHoveredRef = useRef(setHoveredCandle);
  // Selected custom annotation — native click-select doesn't surface our
  // control points, so selection is managed manually (see helpers below).
  const selAnnRef = useRef(null);

  useEffect(() => { autoSaveRef.current = autoSave; }, [autoSave]);

  const isRatio = ticker.includes('/');

  useEffect(() => {
    if (embedded && initialTicker && initialTicker !== ticker) {
      setTicker(initialTicker);
      setInputVal(initialTicker);
      loadChart(initialTicker); // eslint-disable-line
    }
  }, [initialTicker]); // eslint-disable-line

  // ── Save status ───────────────────────────────────────────────────────────
  const showSave = (text, color, autoClear = true) => {
    setSaveStatus({ text, color });
    if (autoClear) setTimeout(() => setSaveStatus({ text: '', color: '' }), 2500);
  };

  // Server preferences follow the account across devices. Browser storage is a fallback.
  useEffect(() => {
    if (!user?.id) { setPatternsLoaded(true); return; }
    let cancelled = false;
    api.get('/users/chart-pattern-settings')
      .then(r => {
        if (!cancelled && r.data.settings && !patternTouchedRef.current)
          setPatternSettings(normaliseSettings(r.data.settings));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPatternsLoaded(true); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const changePatternSettings = next => {
    patternTouchedRef.current = true;
    setPatternSettings(next);
  };

  // Auto-patterns are temporary renderer graphics, not chart.annotations.
  useEffect(() => {
    patternSettingsRef.current = patternSettings;
    try { localStorage.setItem('chartPatterns:v1:' + (user?.id || 'guest'), JSON.stringify(patternSettings)); } catch (_) {}
    patternMatchesRef.current = detectPatterns(patternBarsRef.current, patternSettings);
    if (chartRef.current) drawPatternOverlays(chartRef.current, patternMatchesRef.current, patternBarsRef.current);
    if (!patternsLoaded || !user?.id) return;
    const save = setTimeout(() => {
      api.put('/users/chart-pattern-settings', { settings: patternSettings }).catch(() => {});
    }, 600);
    return () => clearTimeout(save);
  }, [patternSettings, patternsLoaded, user?.id]);

  // ── Annotations ───────────────────────────────────────────────────────────
  const saveAnnotations = useCallback(async (chart, t) => {
    showSave('Saving...', 'var(--warning)', false);
    const data = (chart.annotations || []).map(ann => {
      const opt = JSON.parse(JSON.stringify(ann.userOptions || ann.options || {}));
      if (opt?.custom?.type === 'iRange') return { draggable: '', custom: opt.custom };
      if (opt?.custom?.type === 'ellArc') return { draggable: '', custom: opt.custom };
      if (opt.typeOptions?.points) opt.typeOptions.points.forEach(p => delete p.controlPoint);
      if (opt.controlPoints) delete opt.controlPoints;
      return opt;
    });
    try {
      await api.post('/annotations/save', {
        ticker: t,
        chart_type: t.includes('/') ? 'ratio' : 'stock',
        annotation_data: data,
      });
      showSave('Saved', 'var(--success)');
    } catch (_) { showSave('Error', 'var(--error)'); }
  }, []); // eslint-disable-line

  const scheduleAutoSave = useCallback((chart, t) => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    if (autoSaveRef.current) {
      autoSaveTimer.current = setTimeout(() => saveAnnotations(chart, t), AUTO_SAVE_DELAY);
    }
  }, [saveAnnotations]);

  // ── iRange ────────────────────────────────────────────────────────────────
  // Directional: first click → second click. Positive if p2 is above p1.
  function iRangePct(y1, y2) {
    if (y1 === y2) return '0.00%';
    const pct = ((y2 - y1) / Math.abs(y1)) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  }

  function buildIRange(chart, p1, p2, capX) {
    const cap = Math.max(0, capX || 0), half = cap / 2;
    const label = iRangePct(p1.y, p2.y);
    return {
      draggable: '',
      custom: { type: 'iRange', p1: { x: p1.x, y: p1.y }, p2: { x: p2.x, y: p2.y }, capX: cap },
      shapes: [
        {
          type: 'path', strokeWidth: 2,
          points: [{ xAxis:0, yAxis:0, x:p1.x, y:p1.y }, { xAxis:0, yAxis:0, x:p2.x, y:p2.y }],
          // Visual anchors only — dragging is owned by container listeners.
          controlPoints: [
            { symbol: 'circle', width: 10, height: 10,
              positioner() { const p = cpAnn(this)?.userOptions?.custom?.p1; if (!p) return { x:-9999, y:-9999 }; return { x: chart.xAxis[0].toPixels(p.x)-5, y: chart.yAxis[0].toPixels(p.y)-5 }; } },
            { symbol: 'circle', width: 10, height: 10,
              positioner() { const p = cpAnn(this)?.userOptions?.custom?.p2; if (!p) return { x:-9999, y:-9999 }; return { x: chart.xAxis[0].toPixels(p.x)-5, y: chart.yAxis[0].toPixels(p.y)-5 }; } },
            { symbol: 'square', width: 10, height: 10,
              positioner() { const c = cpAnn(this)?.userOptions?.custom; if (!c?.p1 || !c?.p2) return { x:-9999, y:-9999 }; const pT = (c.p1.y >= c.p2.y) ? c.p1 : c.p2; return { x: chart.xAxis[0].toPixels(pT.x + (c.capX/2))-5, y: chart.yAxis[0].toPixels(pT.y)-5 }; } },
            { symbol: 'diamond', width: 12, height: 12,
              positioner() { const c = cpAnn(this)?.userOptions?.custom; if (!c?.p1 || !c?.p2) return { x:-9999, y:-9999 }; return { x: (chart.xAxis[0].toPixels(c.p1.x) + chart.xAxis[0].toPixels(c.p2.x)) / 2 - 6, y: (chart.yAxis[0].toPixels(c.p1.y) + chart.yAxis[0].toPixels(c.p2.y)) / 2 - 6 }; } },
          ],
        },
        { type:'path', strokeWidth:2, points:[{xAxis:0,yAxis:0,x:p1.x,y:p1.y},{xAxis:0,yAxis:0,x:p2.x,y:p2.y}] },
        { type:'path', strokeWidth:2, points:[{xAxis:0,yAxis:0,x:p1.x-half,y:p1.y},{xAxis:0,yAxis:0,x:p1.x+half,y:p1.y}] },
        { type:'path', strokeWidth:2, points:[{xAxis:0,yAxis:0,x:p2.x-half,y:p2.y},{xAxis:0,yAxis:0,x:p2.x+half,y:p2.y}] },
      ],
      labels: [
        { point:{xAxis:0,yAxis:0,x:p2.x,y:p2.y}, text:label, backgroundColor:css('--surface'), borderColor:css('--border'), borderRadius:3, style:{fontSize:'11px',fontWeight:'600',color:css('--text')}, y:-18 },
      ],
    };
  }

  // ── ellArc ────────────────────────────────────────────────────────────────
  // Half-ellipse arc from p1 to p2. p3 (axis point) controls the perpendicular
  // bulge: its signed pixel distance from the chord midpoint is the arc height.
  function ellArcGeom(chart, c) {
    const x1 = chart.xAxis[0].toPixels(c.p1.x), y1 = chart.yAxis[0].toPixels(c.p1.y);
    const x2 = chart.xAxis[0].toPixels(c.p2.x), y2 = chart.yAxis[0].toPixels(c.p2.y);
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
    let ux = x2 - x1, uy = y2 - y1;
    const L = Math.hypot(ux, uy) || 1;
    ux /= L; uy /= L;
    const nx = -uy, ny = ux;              // unit normal to the chord (pixel space)
    const a = Math.max(L / 2, 1);         // semi-axis along the chord
    let b = a;                            // default bulge: semicircle
    if (c.p3) {
      const x3 = chart.xAxis[0].toPixels(c.p3.x), y3 = chart.yAxis[0].toPixels(c.p3.y);
      b = (x3 - mx) * nx + (y3 - my) * ny;
    }
    return { mx, my, ux, uy, nx, ny, a, b };
  }

  function buildEllArc(chart, p1, p2, p3) {
    const SEG = 48;
    const custom = { type: 'ellArc', p1: { x: p1.x, y: p1.y }, p2: { x: p2.x, y: p2.y } };
    if (p3) custom.p3 = { x: p3.x, y: p3.y };
    const g = ellArcGeom(chart, custom);
    if (!custom.p3) {
      const ax = g.mx + g.nx * g.b, ay = g.my + g.ny * g.b;
      custom.p3 = { x: chart.xAxis[0].toValue(ax), y: chart.yAxis[0].toValue(ay) };
    }
    const pts = [];
    for (let i = 0; i <= SEG; i++) {
      const t = Math.PI * (1 - i / SEG);  // π → 0 sweeps P1 → P2
      const px = g.mx + g.ux * g.a * Math.cos(t) + g.nx * g.b * Math.sin(t);
      const py = g.my + g.uy * g.a * Math.cos(t) + g.ny * g.b * Math.sin(t);
      pts.push({ xAxis: 0, yAxis: 0, x: chart.xAxis[0].toValue(px), y: chart.yAxis[0].toValue(py) });
    }
    return {
      draggable: '',
      custom,
      shapes: [{
        type: 'path', strokeWidth: 2, fill: 'transparent',
        points: pts,
        // Visual anchors only — dragging is owned by container listeners.
        controlPoints: [
          { symbol: 'circle', width: 10, height: 10,
            positioner() { const p = cpAnn(this)?.userOptions?.custom?.p1; if (!p) return { x:-9999, y:-9999 }; return { x: chart.xAxis[0].toPixels(p.x)-5, y: chart.yAxis[0].toPixels(p.y)-5 }; } },
          { symbol: 'circle', width: 10, height: 10,
            positioner() { const p = cpAnn(this)?.userOptions?.custom?.p2; if (!p) return { x:-9999, y:-9999 }; return { x: chart.xAxis[0].toPixels(p.x)-5, y: chart.yAxis[0].toPixels(p.y)-5 }; } },
          { symbol: 'square', width: 10, height: 10,
            positioner() { const c = cpAnn(this)?.userOptions?.custom; if (!c?.p1 || !c?.p2) return { x:-9999, y:-9999 }; const gg = ellArcGeom(chart, c); return { x: gg.mx + gg.nx * gg.b - 5, y: gg.my + gg.ny * gg.b - 5 }; } },
          { symbol: 'diamond', width: 12, height: 12,
            positioner() { const c = cpAnn(this)?.userOptions?.custom; if (!c?.p1 || !c?.p2) return { x:-9999, y:-9999 }; return { x: (chart.xAxis[0].toPixels(c.p1.x) + chart.xAxis[0].toPixels(c.p2.x)) / 2 - 6, y: (chart.yAxis[0].toPixels(c.p1.y) + chart.yAxis[0].toPixels(c.p2.y)) / 2 - 6 }; } },
        ],
      }],
    };
  }

  // ── Custom-annotation selection ───────────────────────────────────────────
  // ControlPoint callbacks: annotation reachable as cp.target (or
  // cp.target.annotation for shape-level points); cp.annotation is not reliable.
  function cpAnn(cp) { return cp.annotation || (cp.target && (cp.target.annotation || cp.target)); }

  function setAnnCPVisible(ann, vis) {
    if (!ann) return;
    try {
      if (typeof ann.setControlPointsVisibility === 'function') ann.setControlPointsVisibility(vis);
      else if (ann.controlPoints) ann.controlPoints.forEach(cp => cp.graphic && cp.graphic.attr({ visibility: vis ? 'inherit' : 'hidden' }));
    } catch (_) {}
  }

  function selectAnnotation(ann) {
    const prev = selAnnRef.current;
    if (prev === ann) return;
    setAnnCPVisible(prev, false);
    selAnnRef.current = ann || null;
    setAnnCPVisible(ann, true);
  }

  function distToSeg(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  }

  // Click hit-test over custom annotations; coords are chart-absolute px.
  function hitTestAnnotations(chart, cx, cy) {
    const TH = 12;
    const xA = chart.xAxis[0], yA = chart.yAxis[0];
    const anns = chart.annotations || [];
    for (let i = anns.length - 1; i >= 0; i--) {
      const c = anns[i].userOptions && anns[i].userOptions.custom;
      if (!c || anns[i].userOptions.visible === false) continue;
      let pxSegs = null;
      if (c.type === 'iRange' && c.p1 && c.p2) {
        const h = (c.capX || 0) / 2;
        const P = (x, y) => [xA.toPixels(x), yA.toPixels(y)];
        pxSegs = [[P(c.p1.x, c.p1.y), P(c.p2.x, c.p2.y)],
                  [P(c.p1.x - h, c.p1.y), P(c.p1.x + h, c.p1.y)],
                  [P(c.p2.x - h, c.p2.y), P(c.p2.x + h, c.p2.y)]];
      } else if (c.type === 'ellArc' && c.p1 && c.p2) {
        const g = ellArcGeom(chart, c), pts = [];
        for (let k = 0; k <= 24; k++) {
          const t = Math.PI * (1 - k / 24);
          pts.push([g.mx + g.ux * g.a * Math.cos(t) + g.nx * g.b * Math.sin(t),
                    g.my + g.uy * g.a * Math.cos(t) + g.ny * g.b * Math.sin(t)]);
        }
        pxSegs = pts.slice(1).map((p, k) => [pts[k], p]);
      }
      if (!pxSegs) continue;
      if (pxSegs.some(([a, b]) => distToSeg(cx, cy, a[0], a[1], b[0], b[1]) <= TH)) return anns[i];
    }
    return null;
  }

  // ── Anchor dragging ───────────────────────────────────────────────────────
  // Highcharts' ControlPoint drag dies on touch, and update() mid-drag destroys
  // its drag context. Anchors are visual only; container capture listeners own
  // the gesture and rebuild the annotation on every move.
  function pxToData(chart, px, py) {
    return { x: chart.xAxis[0].toValue(px), y: chart.yAxis[0].toValue(py) };
  }

  // Anchor positions in chart px, keyed by id. Mirrors the positioners.
  function anchorPositions(chart, c) {
    const xA = chart.xAxis[0], yA = chart.yAxis[0];
    const P = (x, y) => [xA.toPixels(x), yA.toPixels(y)];
    const [x1, y1] = P(c.p1.x, c.p1.y), [x2, y2] = P(c.p2.x, c.p2.y);
    const out = { p1: [x1, y1], p2: [x2, y2], mid: [(x1 + x2) / 2, (y1 + y2) / 2] };
    if (c.type === 'iRange') {
      const pT = (c.p1.y >= c.p2.y) ? c.p1 : c.p2;
      out.cap = P(pT.x + (c.capX || 0) / 2, pT.y);
    } else {
      const g = ellArcGeom(chart, c);
      out.p3 = [g.mx + g.nx * g.b, g.my + g.ny * g.b];
    }
    return out;
  }

  // Apply a drag of anchor `id`: pointer moved startPx→curPx (chart px);
  // startCustom is a deep copy of custom taken at drag start.
  function applyAnchorDrag(chart, ann, id, startPx, curPx, startCustom) {
    const c = ann.userOptions.custom;
    const s = pxToData(chart, startPx[0], startPx[1]);
    const cur = pxToData(chart, curPx[0], curPx[1]);
    const dx = cur.x - s.x, dy = cur.y - s.y;
    if (id === 'p1')      { c.p1.x = startCustom.p1.x + dx; c.p1.y = startCustom.p1.y + dy; }
    else if (id === 'p2') { c.p2.x = startCustom.p2.x + dx; c.p2.y = startCustom.p2.y + dy; }
    else if (id === 'p3') { c.p3 = { x: cur.x, y: cur.y }; }
    else if (id === 'cap') {
      const pT = (startCustom.p1.y >= startCustom.p2.y) ? startCustom.p1 : startCustom.p2;
      c.capX = Math.max(0, Math.abs(cur.x - pT.x) * 2);
    }
    else if (id === 'mid') {
      c.p1 = { x: startCustom.p1.x + dx, y: startCustom.p1.y + dy };
      c.p2 = { x: startCustom.p2.x + dx, y: startCustom.p2.y + dy };
      if (startCustom.p3) c.p3 = { x: startCustom.p3.x + dx, y: startCustom.p3.y + dy };
    }
    ann.update(c.type === 'iRange' ? buildIRange(chart, c.p1, c.p2, c.capX) : buildEllArc(chart, c.p1, c.p2, c.p3));
    setAnnCPVisible(ann, true);
  }

  // ── Y-axis drag ───────────────────────────────────────────────────────────
  function setupYAxisDrag(chart) {
    const axes = chart.yAxis.filter(a => a.userOptions.id !== 'navigator-y-axis');
    axes.forEach(axis => {
      axis.axisRect = chart.renderer.rect()
        .attr({ fill:'rgba(124,58,237,0.04)', stroke:'rgba(124,58,237,0.15)', 'stroke-width':1, zIndex:7 })
        .css({ cursor:'ns-resize' }).add();
      axis.axisRect.element.addEventListener('wheel', e => {
        e.preventDefault();
        const r = axis.max - axis.min, ch = r * (e.deltaY > 0 ? 0.1 : -0.1);
        axis.setExtremes(axis.min - ch, axis.max + ch, true, false);
      }, { passive: false });
      axis.axisRect.on('mousedown', () => axis._drag = true);
    });
    HC.addEvent(chart, 'render', () => {
      axes.forEach(a => {
        const x = chart.plotLeft + chart.plotWidth;
        a.axisRect.attr({ x, y: a.top, width: chart.chartWidth - x, height: a.height });
      });
    });
    document.addEventListener('mousemove', e => {
      const d = axes.find(a => a._drag);
      if (!d || !e.movementY) return;
      const shift = e.movementY * ((d.max - d.min) / d.height);
      d.setExtremes(d.min + shift, d.max + shift, true, false);
    });
    document.addEventListener('mouseup', () => axes.forEach(a => a._drag = false));
  }

  function setupResetBtn(chart, btnRef) {
    HC.addEvent(chart, 'render', () => {
      const btn = btnRef.current;
      console.log('reset-btn render fired, btn=', btn);
      if (!btn) return;
      btn.onclick = () => chart.yAxis.forEach(a => a.setExtremes(null, null));
      btn.style.left          = (chart.plotLeft + chart.plotWidth  - 42) + 'px';
      btn.style.top           = (chart.plotTop  + chart.plotHeight - 42) + 'px';
      btn.style.opacity       = '1';
      btn.style.pointerEvents = 'auto';
    });
  }

  // ── Grouping ────────────────────────────────────────────────�─────────────
  function applyGrouping(chart, key, btn) {
    document.querySelectorAll('[class*="highcharts-grouping-"]').forEach(el => el.classList.remove('is-active'));
    if (btn) btn.classList.add('is-active');
    const units = { '1D':null, '1W':[['week',[1]]], '1M':[['month',[1]]], '6M':[['month',[6]]], '12M':[['month',[12]]] };
    const cfg = units[key] ? { enabled:true, forced:true, units:units[key] } : { enabled:false };
    chart.series.forEach(s => {
      if (s.options.id && !s.options.id.includes('future')) s.update({ dataGrouping: cfg }, false);
    });
    chart.redraw();
  }

  // ── Render chart ──────────────────────────────────────────────────────────
  const renderChart = useCallback((ohlcv, title, chartTicker) => {
    if (!HC || !chartDivRef.current) { setLoading(false); return; }
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

    patternBarsRef.current = ohlcv;
    patternMatchesRef.current = detectPatterns(ohlcv, patternSettingsRef.current);
    const ohlc   = ohlcv.map(r => [r[0], r[1], r[2], r[3], r[4]]);
    const hasVol = ohlcv[0]?.length > 5;
    const vol    = hasVol ? ohlcv.map(r => [r[0], r[5] || 0]) : null;

    const lastTs  = ohlc[ohlc.length - 1][0];
    const initMin = Math.max(ohlc[0][0], lastTs - 18 * 30.5 * 24 * 60 * 60 * 1000);
    const lastC   = ohlc[ohlc.length - 1][4];

    // Flat candles extending 1 year past the last one, giving room to draw
    // into the future. Skipped on touch devices: ~365 empty ordinal slots
    // make pinch-zoom commit to a wrong range on release.
    const future = [];
    if (!matchMedia('(pointer: coarse)').matches) {
      let cur = lastTs + 86400000;
      while (cur < lastTs + ONE_YEAR_MS) { future.push([cur, lastC, lastC, lastC, lastC]); cur += 86400000; }
    }

    HC.setOptions(HC.merge(hcTheme(), {
      plotOptions: {
        candlestick: { dataGrouping:{ enabled:false } }
      }
    }));

    const yAxes  = [{ labels:{ align:'left' }, height: vol ? '88%' : '100%', startOnTick:false, endOnTick:false }];
    const series = [
      { type:'candlestick', id:'main',   name:title, data:ohlc,   dataGrouping:{ enabled:false } },
      { type:'candlestick', id:'future', data:future, enableMouseTracking:false, showInLegend:false, color:'transparent', upColor:'transparent', borderColor:'transparent', lineColor:'transparent', dataGrouping:{ enabled:false } },
    ];
    if (vol) {
      yAxes.push({ labels:{ align:'left' }, top:'90%', height:'10%', offset:0, startOnTick:false, endOnTick:false });
      series.push({ type:'column', id:'volume',     name:'Volume', data:vol,                       yAxis:1, dataGrouping:{ enabled:false } });
      series.push({ type:'column', id:'future-vol', data:future.map(r=>[r[0],0]), yAxis:1, enableMouseTracking:false, showInLegend:false, color:'transparent', dataGrouping:{ enabled:false } });
    }

    // ── OHLCV readout helpers ───────────────────────────────────────────────
    const findPoint = (id, x) =>
      chartRef.current?.series?.find(s => s.options.id === id)?.points?.find(p => p.x === x);
    const showCandle = (cp, volY) => {
      if (!cp) return;
      const d = new Date(cp.x);
      const dateStr = d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
      setHoveredRef.current({
        date:   dateStr,
        open:   cp.open,
        high:   cp.high,
        low:    cp.low,
        close:  cp.close,
        volume: volY ?? null,
      });
    };

    const chart = HC.stockChart(chartDivRef.current, {
      credits:       { enabled: false },
      title:         { text: '' },
      rangeSelector: { enabled: false },
      tooltip:       { enabled: false },
      chart: {
        height: chartDivRef.current.getBoundingClientRect().height || (window.innerHeight - chartDivRef.current.getBoundingClientRect().top),
        panning: { enabled: true, type: 'x' },
        events: {
          load: async function () {
            const c = this;
            c.xAxis[0].setExtremes(initMin, lastTs + ONE_YEAR_MS * 0.1);
            setupYAxisDrag(c);
            setupResetBtn(c, resetBtnRef);

            const btn1d   = c.container.querySelector('.highcharts-grouping-1d');
            const btnAuto = c.container.querySelector('.highcharts-toggle-autosave');
            if (btn1d)   { document.querySelectorAll('[class*="highcharts-grouping-"]').forEach(el => el.classList.remove('is-active')); btn1d.classList.add('is-active'); }
            if (btnAuto) btnAuto.classList.toggle('is-active', autoSaveRef.current);

            let booting = true;
            const origAdd = c.addAnnotation.bind(c);
            const origRem = c.removeAnnotation.bind(c);
            c.addAnnotation    = function() { const r = origAdd(...arguments);    if (!booting) scheduleAutoSave(c, chartTicker); return r; };
            c.removeAnnotation = function() { const r = origRem(...arguments); if (!booting) scheduleAutoSave(c, chartTicker); return r; };

            try {
              const res = await api.get(`/annotations/load?ticker=${encodeURIComponent(chartTicker)}`);
              const arr = res.data.annotation?.data || [];
              arr.forEach(a => {
                if (a?.custom?.type === 'iRange' && a.custom.p1 && a.custom.p2) {
                  c.addAnnotation(buildIRange(c, a.custom.p1, a.custom.p2, a.custom.capX || 0));
                } else if (a?.custom?.type === 'ellArc' && a.custom.p1 && a.custom.p2) {
                  c.addAnnotation(buildEllArc(c, a.custom.p1, a.custom.p2, a.custom.p3 || null));
                } else {
                  if (a?.controlPoints) delete a.controlPoints;
                  c.addAnnotation(a);
                }
              });
            } catch (_) {}

            booting = false;
            setLoading(false);
          }
        }
      },
      xAxis: {
        ordinal: true,
        overscroll: ONE_YEAR_MS,
        crosshair: { width: 1, color: css('--text-faint'), dashStyle: 'Solid' },
      },
      yAxis: yAxes,
      series,
      plotOptions: {
        candlestick: {
          point: {
            events: {
              // Skip touch-originated hovers — re-rendering mid-tap makes the
              // browser swallow the click the drawing bindings need (see
              // touch-tap guard above). Taps update the readout via `click`.
              mouseOver: function (e) { if (touchish(e)) return; showCandle(this, findPoint('volume', this.x)?.y); },
              mouseOut:  function (e) { if (touchish(e)) return; setHoveredRef.current(null); },
              click:     function ()  { showCandle(this, findPoint('volume', this.x)?.y); }
            }
          }
        },
        column: {
          point: {
            events: {
              mouseOver: function (e) { if (touchish(e)) return; showCandle(findPoint('main', this.x), this.y); },
              mouseOut:  function (e) { if (touchish(e)) return; setHoveredRef.current(null); },
              click:     function ()  { showCandle(findPoint('main', this.x), this.y); }
            }
          }
        }
      },
      stockTools: {
        gui: {
          enabled: true,
          buttons: ['saveAnnotations','toggleAutoSave','separator','grouping1D','grouping1W','grouping1M','grouping6M','grouping12M','iRange','ellArc','separator','indicators','separator','simpleShapes','lines','crookedLines','measure','advanced','toggleAnnotations','separator','verticalLabels','flags','separator','zoomChange','fullScreen','typeChange','separator','currentPriceIndicator'],
          definitions: {
            saveAnnotations: { className:'highcharts-save-annotations' },
            toggleAutoSave:  { className:'highcharts-toggle-autosave'  },
            grouping1D:      { className:'highcharts-grouping-1d'      },
            grouping1W:      { className:'highcharts-grouping-1w'      },
            grouping1M:      { className:'highcharts-grouping-1m'      },
            grouping6M:      { className:'highcharts-grouping-6m'      },
            grouping12M:     { className:'highcharts-grouping-12m'     },
            iRange:          { className:'highcharts-i-range'          },
            ellArc:          { className:'highcharts-ell-arc'          },
          }
        }
      },
      navigation: {
        bindings: {
          saveAnnotations: {
            className: 'highcharts-save-annotations',
            init(b) { saveAnnotations(chartRef.current, chartTicker); HC.fireEvent(this, 'deselectButton', { button: b }); }
          },
          toggleAutoSave: {
            className: 'highcharts-toggle-autosave',
            init(b) {
              const v = !autoSaveRef.current;
              setAutoSave(v);
              localStorage.setItem('autoSave', v);
              b.classList.toggle('is-active', v);
              if (v) saveAnnotations(chartRef.current, chartTicker);
              HC.fireEvent(this, 'deselectButton', { button: b });
            }
          },
          grouping1D:  { className:'highcharts-grouping-1d',  init(b) { applyGrouping(this.chart, '1D',  b); } },
          grouping1W:  { className:'highcharts-grouping-1w',  init(b) { applyGrouping(this.chart, '1W',  b); } },
          grouping1M:  { className:'highcharts-grouping-1m',  init(b) { applyGrouping(this.chart, '1M',  b); } },
          grouping6M:  { className:'highcharts-grouping-6m',  init(b) { applyGrouping(this.chart, '6M',  b); } },
          grouping12M: { className:'highcharts-grouping-12m', init(b) { applyGrouping(this.chart, '12M', b); } },
          iRange: {
            className: 'highcharts-i-range',
            start(e) {
              const c = this.chart, coords = c.pointer.getCoordinates(e);
              const x = coords?.xAxis?.[0]?.value, y = coords?.yAxis?.[0]?.value;
              if (x === undefined || y === undefined) return false;
              this._p1 = { x, y }; return true;
            },
            steps: [function (e) {
              if (e.type !== 'click') return;
              const c = this.chart, p1 = this._p1;
              const coords = c.pointer.getCoordinates(e);
              const x = coords?.xAxis?.[0]?.value, y = coords?.yAxis?.[0]?.value;
              if (!p1 || x === undefined || y === undefined) return;
              const capX = Math.abs(c.xAxis[0].toValue(c.plotLeft + 40) - c.xAxis[0].toValue(c.plotLeft));
              const ann = c.addAnnotation(buildIRange(c, p1, { x, y }, capX));
              selectAnnotation(ann);
              this._p1 = null;
            }]
          },
          ellArc: {
            className: 'highcharts-ell-arc',
            start(e) {
              const c = this.chart, coords = c.pointer.getCoordinates(e);
              const x = coords?.xAxis?.[0]?.value, y = coords?.yAxis?.[0]?.value;
              if (x === undefined || y === undefined) return false;
              this._p1 = { x, y }; return true;
            },
            steps: [function (e) {
              if (e.type !== 'click') return;
              const c = this.chart, p1 = this._p1;
              const coords = c.pointer.getCoordinates(e);
              const x = coords?.xAxis?.[0]?.value, y = coords?.yAxis?.[0]?.value;
              if (!p1 || x === undefined || y === undefined) return;
              const ann = c.addAnnotation(buildEllArc(c, p1, { x, y }, null));
              selectAnnotation(ann);
              this._p1 = null;
            }]
          },
        },
        events: {
          selectButton:   function(e) { const t = e.button?.parentNode?.parentNode; if (t && !t.classList.contains('highcharts-menu-wrapper')) t.classList.add('highcharts-active'); },
          deselectButton: function(e) { const t = e.button?.parentNode?.parentNode; if (t) t.classList.remove('highcharts-active'); },
        }
      }
    });

    chartRef.current = chart;
    selAnnRef.current = null;
    HC.addEvent(chart, 'render', () => {
      if (chartRef.current === chart) drawPatternOverlays(chart, patternMatchesRef.current, patternBarsRef.current);
    });
    drawPatternOverlays(chart, patternMatchesRef.current, patternBarsRef.current);

    // Click-to-select for custom annotations. The tap shim converts mobile
    // taps into clicks, so this one listener covers both. Ignores drags
    // (pan/anchor-drag end in a click), toolbar/popup/navigator clicks, and
    // clicks while a drawing tool is mid-gesture.
    let downPt = null, lastAnchorDragAt = 0;
    chart.container.addEventListener('mousedown', (e) => { downPt = [e.clientX, e.clientY]; }, true);
    chart.container.addEventListener('click', (e) => {
      if (chart.navigationBindings?.selectedButton) return;
      if (Date.now() - lastAnchorDragAt < 400) return;
      if (downPt && Math.hypot(e.clientX - downPt[0], e.clientY - downPt[1]) > 6) return;
      if (e.target.closest?.('.highcharts-menu-item-btn, .highcharts-menu-wrapper, .highcharts-submenu-wrapper, .highcharts-popup, .highcharts-navigator, .highcharts-scrollbar')) return;
      const r = chart.container.getBoundingClientRect();
      selectAnnotation(hitTestAnnotations(chart, e.clientX - r.left, e.clientY - r.top));
    });

    // Anchor dragging. Capture-phase press near an anchor of the selected
    // annotation claims the gesture (stopPropagation kills HC pan; the tap
    // shim's >10px move guard suppresses its synthetic click), then the
    // annotation is rebuilt on every move from data-space deltas.
    const drag = { active: false, ann: null, id: null, startPx: null, startCustom: null };
    const evtPx = (e) => {
      const src = e.touches ? e.touches[0] : e;
      const r = chart.container.getBoundingClientRect();
      return [src.clientX - r.left, src.clientY - r.top];
    };
    const startDrag = (e) => {
      if (e.target.closest?.('.highcharts-popup')) return;
      const ann = selAnnRef.current;
      const c = ann?.userOptions?.custom;
      if (!c?.p1 || !c?.p2) return;
      const [px, py] = evtPx(e);
      const TH = e.type === 'touchstart' ? 16 : 10;
      let bestId = null, bestD = Infinity;
      for (const [id, [ax, ay]] of Object.entries(anchorPositions(chart, c))) {
        const d = Math.hypot(px - ax, py - ay);
        if (d < bestD) { bestD = d; bestId = id; }
      }
      if (!bestId || bestD > TH) return;
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      drag.active = true; drag.ann = ann; drag.id = bestId;
      drag.startPx = [px, py];
      drag.startCustom = JSON.parse(JSON.stringify(c));
    };
    const moveDrag = (e) => {
      if (!drag.active) return;
      if (e.cancelable) e.preventDefault();
      applyAnchorDrag(chart, drag.ann, drag.id, drag.startPx, evtPx(e), drag.startCustom);
    };
    const endDrag = () => {
      if (drag.active) { selectAnnotation(drag.ann); lastAnchorDragAt = Date.now(); }
      drag.active = false; drag.ann = null; drag.id = null;
    };
    chart.container.addEventListener('touchstart', startDrag, { capture: true, passive: false });
    chart.container.addEventListener('mousedown', startDrag, true);
    window.addEventListener('touchmove', moveDrag, { passive: false });
    window.addEventListener('mousemove', moveDrag);
    window.addEventListener('touchend', endDrag);
    window.addEventListener('mouseup', endDrag);

    // Keep the chart matched to its container. The chart is created with an
    // explicit pixel height, but mobile browser chrome showing/hiding resizes
    // the container without reliably firing window resize — the stale height
    // leaves the navigator and toolbar arrows clipped off the bottom.
    const sizeTarget = chartDivRef.current;
    if (window.ResizeObserver && sizeTarget && !sizeTarget._sizeObs) {
      sizeTarget._sizeObs = new ResizeObserver(() => {
        const c = chartRef.current;
        if (!c) return;
        const r = sizeTarget.getBoundingClientRect();
        if (r.width && r.height && (Math.round(r.width) !== c.chartWidth || Math.round(r.height) !== c.chartHeight)) {
          c.setSize(r.width, r.height, false);
        }
      });
      sizeTarget._sizeObs.observe(sizeTarget);
    }

    // ── Tap shim ─────────────────────────────────────────────────────────
    // Mobile browsers frequently never deliver the 'click' that Highcharts'
    // stock-tools bindings (toolbar buttons, two-step drawing tools) rely on
    // — a tap gets treated as pure hover whenever the browser's heuristics
    // decide the page "handled" it. Selection/dragging still works because
    // those run on touch events, which is why existing annotations can be
    // edited while new ones can't be drawn. Watch taps on the chart area; if
    // no real click follows a clean tap within 300ms, synthesize one at the
    // same spot. Where clicks arrive normally this shim does nothing.
    const rootEl = chartDivRef.current;
    if (rootEl && !rootEl._tapShim) {
      rootEl._tapShim = true;
      let tap = null, lastClickAt = 0;
      rootEl.addEventListener('click', () => { lastClickAt = Date.now(); }, { capture: true });
      rootEl.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) { tap = null; return; }
        const t = e.touches[0];
        tap = { x: t.clientX, y: t.clientY, at: Date.now(), moved: false };
      }, { passive: true, capture: true });
      rootEl.addEventListener('touchmove', (e) => {
        if (!tap) return;
        const t = e.touches[0];
        if (t && Math.hypot(t.clientX - tap.x, t.clientY - tap.y) > 10) tap.moved = true;
      }, { passive: true, capture: true });
      rootEl.addEventListener('touchcancel', () => { tap = null; }, { passive: true, capture: true });
      rootEl.addEventListener('touchend', (e) => {
        const info = tap; tap = null;
        if (!info || info.moved || Date.now() - info.at > 700) return;
        const t = e.changedTouches[0];
        if (!t) return;
        setTimeout(() => {
          if (Date.now() - lastClickAt < 400) return;  // browser delivered a real click
          const target = document.elementFromPoint(t.clientX, t.clientY);
          if (target && rootEl.contains(target)) {
            target.dispatchEvent(new MouseEvent('click', {
              bubbles: true, cancelable: true, view: window,
              clientX: t.clientX, clientY: t.clientY,
            }));
          }
        }, 300);
      }, { passive: true, capture: true });
    }
  }, [saveAnnotations, scheduleAutoSave]); // eslint-disable-line

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadChart = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    setHoveredCandle(null);
    setSaveStatus({ text: '', color: '' });
    try {
      if (t.includes('/')) {
        const [t1, t2] = t.split('/');
        const r = await api.get(`/chart/ratio?t1=${encodeURIComponent(t1)}&t2=${encodeURIComponent(t2)}`);
        setChartInfo(null);
        renderChart(r.data.ohlcv, r.data.pair, t);
      } else {
        const [dataRes, infoRes] = await Promise.all([
          api.get(`/chart/data?ticker=${encodeURIComponent(t)}`),
          api.get(`/chart/info?ticker=${encodeURIComponent(t)}`),
        ]);
        setChartInfo(infoRes.data);
        renderChart(dataRes.data.ohlcv, dataRes.data.name || t, t);
      }
    } catch (_) {
      setLoading(false);
    }
  }, [renderChart]);

  // ── Search ────────────────────────────────────────────────────────────────
  const doSearch = useCallback((q) => {
    if (abortRef.current) abortRef.current.abort();
    if (!q || q.length < 1) { setSearchResults([]); setShowDropdown(false); return; }
    abortRef.current = new AbortController();
    setSearching(true);
    api.get(`/chart/search?q=${encodeURIComponent(q)}`, { signal: abortRef.current.signal })
      .then(r => { setSearchResults(r.data); setShowDropdown(r.data.length > 0); })
      .catch(() => {})
      .finally(() => setSearching(false));
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.includes('/')) { setShowDropdown(false); return; }
    searchTimer.current = setTimeout(() => doSearch(v), SEARCH_DEBOUNCE);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTimer.current) clearTimeout(searchTimer.current);
      if (abortRef.current) abortRef.current.abort();
      setShowDropdown(false);
      const val = inputVal.trim().toUpperCase();
      if (val) {
        setTicker(val);
        loadChart(val);
        if (onTickerChange) onTickerChange(val);
      }
    }
    if (e.key === 'Escape') setShowDropdown(false);
  };

  const handleSelectResult = (r) => {
    const t = `${r.symbol}:${r.exchange}`;
    setInputVal(t); setTicker(t); setShowDropdown(false);
    loadChart(t);
    if (onTickerChange) onTickerChange(t);
  };

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadChart(ticker);
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (refreshKey > 0) loadChart(ticker);
  }, [refreshKey]); // eslint-disable-line


  const badgeColor = (exch) => exch === 'AU'
    ? { background: 'var(--info-dim)', color: 'var(--info)' }
    : { background: 'var(--warning-dim)', color: 'var(--warning)' };

  return (
    <>
      <style>{KEYFRAMES}</style>
      <style>{HC_CSS}</style>
      <div className="chart-page" style={S.page}>

        {!embedded && <Navbar />}

        <div className="cp-controls" style={S.controls}>
          <div className="cp-search" style={S.searchWrap}>
            <input
              style={{
                ...S.searchInput,
                borderColor: showDropdown ? 'var(--accent)' : 'var(--border)',
                boxShadow:   showDropdown ? '0 0 0 3px var(--accent-dim)' : 'none',
              }}
              value={inputVal}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="BHP:AU  AAPL:US  BHP:AU/CBA:AU"
              spellCheck={false}
              autoCapitalize="characters"
            />
            {searching && <span style={S.searchDot} />}
           {showDropdown && searchResults.length > 0 && (
              <div style={S.dropdown}>
                {searchResults.map((r, i) => (
                  <div
                    key={i}
                    style={S.dropItem}
                    onMouseDown={() => handleSelectResult(r)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
                  >
                    <span style={S.dropSymbol}>{r.symbol}</span>
                    <span style={S.dropName}>{r.name}</span>
                    <span style={{ ...S.badge, ...badgeColor(r.exchange) }}>{r.exchange}</span>
                    {['ETF','etf'].includes(r.asset_type) && (
                      <span style={{ ...S.badge, background:'var(--accent-dim)', color:'var(--accent)' }}>ETF</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <span className="cp-ticker" style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:"'JetBrains Mono',monospace" }}>
            {ticker}
          </span>
          {!isRatio && (
            <>
              <button style={S.addListBtn} onClick={() => setShowListModal(true)}>+ List</button>
              <button style={{...S.addListBtn, ...(patternSettings.enabled ? {color:'var(--accent)',borderColor:'var(--accent)'} : {})}}
                      onClick={() => setShowPatternPanel(true)} title="Rule-based chart pattern recognition">
                Auto Patterns {patternSettings.enabled ? 'ON' : 'OFF'} ▾
              </button>
              {/* Company info page (announcements, performance) in a new tab. */}
              <button style={S.addListBtn} title="Open company page in a new tab"
                      onClick={() => window.open(`/company?ticker=${encodeURIComponent(ticker)}`, '_blank', 'noopener')}>
                Company ↗
              </button>
              <AnalystBar ticker={ticker} currentPrice={chartInfo?.snapshot?.close ?? null} />
            </>
          )}
        </div>

        <InfoBar info={chartInfo} isRatio={isRatio} pair={ticker} />

        <div style={S.chartWrap}>
          {loading && (
            <div style={S.overlay}><div style={S.spinner} /></div>
          )}
          {saveStatus.text && (
            <div style={{ ...S.saveStatus, color: saveStatus.color }}>{saveStatus.text}</div>
          )}
          <div ref={chartDivRef} style={S.chartDiv} />
          <OhlcvDisplay candle={hoveredCandle} />
          <button ref={resetBtnRef} style={S.resetBtn} title="Reset scale">↻</button>
        </div>

      </div>

      {showListModal && (
        <AddToListModal ticker={ticker} onClose={() => setShowListModal(false)} />
      )}
      {showPatternPanel && (
        <ChartPatternSettings settings={patternSettings} onChange={changePatternSettings}
                              onClose={() => setShowPatternPanel(false)} />
      )}
    </>
  );
}
